import { useCallback } from 'react';

import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import InfoRounded from '@mui/icons-material/InfoRounded';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import type { Theme } from '@mui/material';
import { CircularProgress } from '@mui/material';
import type { SnackbarKey } from 'notistack';
import { useSnackbar } from 'notistack';

declare module 'notistack' {
  interface VariantOverrides {
    warning: true;
    loading: true;
    default: false;
  }
}

const useResponseMessage = () => {
  const { closeSnackbar } = useSnackbar();

  const handleDismiss = useCallback(
    (id: SnackbarKey) => {
      closeSnackbar(id);
    },
    [closeSnackbar]
  );

  const messages = {
    loading: 'Loading your data, please wait...',
    info: 'Nothing to display here. Try changing your filters or adding new data.',
    error: 'Oops! Something went wrong. Please try again later.',
    success: 'Changes saved!',
    warning: ''
  };

  const icons = {
    loading: <CircularProgress size='20px' />,
    info: <InfoRounded sx={theme => ({ fontSize: 16, color: theme.palette.info.light })} />,
    error: <Cancel sx={theme => ({ fontSize: 16, color: theme.palette.error.main })} />,
    success: <CheckCircle sx={theme => ({ fontSize: 16, color: theme.palette.success.main })} />,
    warning: (
      <WarningAmberOutlinedIcon
        sx={theme => ({ fontSize: 16, color: theme.palette.warning.main })}
      />
    )
  };

  const borderColours = (theme: Theme) => {
    return {
      loading: theme.palette.primary.main,
      info: theme.palette.info.light,
      error: theme.palette.error.main,
      success: theme.palette.success.main,
      warning: theme.palette.warning.main
    };
  };

  return {
    handleDismiss,
    messages,
    icons,
    borderColours
  };
};

export default useResponseMessage;
